import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import Preloader from '@/components/Preloader';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { usePode } from '@/stores/authStore';
import FormularioVerba from './FormularioVerba';
import { erroDaApi, type Verba, type Verbas } from './tipos';

/**
 * As linhas da planilha do ERP e o que compõe cada uma: "VL SALARIO = 001 +
 * 007 + 452". Rubrica que não cabe em nenhuma delas ganha verba nova aqui.
 */
export default function VerbasDaFolha() {
  const podeConfigurar = usePode('folha.configurar');
  const queryClient = useQueryClient();
  // undefined: fechado; null: verba nova.
  const [editando, setEditando] = useState<Verba | null | undefined>(undefined);
  const [excluindo, setExcluindo] = useState<Verba | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['folha-verbas'],
    queryFn: async () => (await api.get<{ data: Verbas }>('/folha/verbas')).data.data,
  });

  // O servidor recalcula as competências abertas a cada mudança aqui.
  const recarregar = () => {
    for (const chave of ['folha-verbas', 'folha-vinculos', 'folha', 'folha-conferencia', 'folha-lancamentos']) {
      void queryClient.invalidateQueries({ queryKey: [chave] });
    }
  };

  const excluir = useMutation({
    mutationFn: async (verba: Verba) => (await api.delete(`/folha/verbas/${verba.id}`)).data,
    onSuccess: () => {
      setExcluindo(null);
      recarregar();
    },
  });

  if (isLoading) {
    return <Preloader telaCheia />;
  }

  if (isError || !data) {
    return <p className="py-lg text-[14px] text-danger">Não foi possível carregar as verbas.</p>;
  }

  return (
    <>
      <div className="mb-sm flex flex-wrap items-center justify-between gap-sm">
        <p className="text-[13px] text-muted">
          Cada verba vira uma linha da planilha do ERP por centro de custo, somando as rubricas que apontam para ela.
        </p>
        {podeConfigurar && (
          <button
            type="button"
            onClick={() => setEditando(null)}
            className="flex h-9 items-center gap-sm rounded-lg bg-accent px-md text-[13px] font-bold text-white hover:bg-accent/90"
          >
            <Plus size={15} />
            Nova verba
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-surface">
        <table className="w-full text-[13px]">
          <thead className="border-b-2 border-border bg-surface-subtle text-[11px] tracking-wide text-ink-soft uppercase">
            <tr>
              <th className="px-sm py-sm text-left font-bold">Verba</th>
              <th className="px-sm py-sm text-left font-bold">Composição</th>
              <th className="px-sm py-sm text-left font-bold whitespace-nowrap">Débito / crédito</th>
              <th className="w-[76px] px-sm py-sm" />
            </tr>
          </thead>
          <tbody>
            {data.verbas.map((verba) => (
              <tr key={verba.id} className="border-b border-border align-top last:border-0 even:bg-dominant">
                <td className="px-sm py-xs">
                  <span className="block font-bold text-ink">{verba.nome}</span>
                  <span className="block text-[12px] text-muted">
                    {verba.linhas} {verba.linhas === 1 ? 'linha' : 'linhas'} na planilha
                  </span>
                </td>
                <td className="px-sm py-xs">
                  {verba.origem === 'rubricas' ? (
                    verba.rubricas.length === 0 ? (
                      <span className="text-muted">nenhuma rubrica ainda — sai zerada</span>
                    ) : (
                      <span className="flex flex-wrap items-center gap-xs">
                        {verba.rubricas.map((rubrica, indice) => (
                          <span key={rubrica.codigo} className="flex items-center gap-xs">
                            {indice > 0 && <span className="text-muted">+</span>}
                            <span
                              title={rubrica.descricao}
                              className="rounded-full border border-border bg-surface-subtle px-sm py-[1px] text-[12px] font-bold text-ink tabular-nums"
                            >
                              {rubrica.codigo}
                            </span>
                          </span>
                        ))}
                        {verba.colunas === 'ativos_outros' && <span className="text-[12px] text-muted">(ativos + afastados)</span>}
                      </span>
                    )
                  ) : (
                    <span className="text-muted">{verba.regra}</span>
                  )}
                </td>
                <td className="px-sm py-xs whitespace-nowrap tabular-nums">
                  <span className="text-ink">
                    {verba.conta_debito} / {verba.conta_credito}
                  </span>
                  {verba.contas.map((conta) => (
                    <span key={conta.centro_custo_id} className="block text-[12px] text-muted">
                      {conta.conta_debito} / {conta.conta_credito} em {conta.centro_custo}
                    </span>
                  ))}
                </td>
                <td className="px-sm py-xs text-right whitespace-nowrap">
                  {podeConfigurar && (
                    <>
                      <button
                        type="button"
                        onClick={() => setEditando(verba)}
                        title="Editar"
                        className="rounded-md p-[6px] text-muted hover:bg-accent/10 hover:text-accent"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          excluir.reset();
                          setExcluindo(verba);
                        }}
                        disabled={verba.impede_exclusao !== null}
                        title={verba.impede_exclusao ? `Não dá para excluir: ${verba.impede_exclusao}` : 'Excluir'}
                        className="rounded-md p-[6px] text-muted hover:bg-danger/10 hover:text-danger disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted"
                      >
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editando !== undefined && (
        <FormularioVerba
          key={editando?.id ?? 'nova'}
          verba={editando}
          centros={data.centros_custo}
          aoFechar={() => setEditando(undefined)}
          aoSalvar={() => {
            setEditando(undefined);
            recarregar();
          }}
        />
      )}

      <Dialog open={excluindo !== null} onOpenChange={(aberto) => !excluir.isPending && !aberto && setExcluindo(null)}>
        <DialogContent className="max-w-[460px] p-lg">
          <DialogTitle>Excluir a verba {excluindo?.nome}?</DialogTitle>
          <DialogDescription className="mt-xs">
            As {excluindo?.linhas} linhas dela saem da planilha do ERP nas competências que ainda não foram enviadas. Não dá
            para desfazer.
          </DialogDescription>
          {excluir.isError && (
            <p className="mt-sm text-[13px] font-bold text-danger">{erroDaApi(excluir.error, 'Não foi possível excluir a verba.')}</p>
          )}
          <div className="mt-lg flex justify-end gap-sm">
            <button
              type="button"
              onClick={() => setExcluindo(null)}
              disabled={excluir.isPending}
              className="rounded-md border border-border px-md py-sm text-[13px] font-bold text-ink hover:bg-surface-subtle"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => excluindo && excluir.mutate(excluindo)}
              disabled={excluir.isPending}
              className="inline-flex items-center gap-xs rounded-md bg-danger px-md py-sm text-[13px] font-bold text-white hover:bg-danger/90 disabled:opacity-50"
            >
              <Trash2 size={14} aria-hidden="true" />
              {excluir.isPending ? 'Excluindo…' : 'Excluir verba'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
